// facts/assemble.ts - assembles the FULL EngineFacts for one decision, and computes the cross-street
// `whatChanged` delta. ENGINE_DESIGN.md §8.1; ARCHITECTURE.md §5, §9 (killer feature 1).
//
// This is the LAST engine stage before the coach boundary. Everything upstream has already run - the
// hand machine produced the DecisionPoint, range narrowed the villain, equity computed hero-vs-range,
// strategy produced the RecommendedAction, grading produced the DecisionGrade. assembleFacts stitches
// those pre-computed pieces into the one validated record that is stored per-decision in D1 and
// projected (digest.ts) to the coach. It OWNS the bucket derivations (spr/equity/delta) and the price
// statement; it never re-runs strategy or equity (same injection posture as the rest of the engine).
import { EngineFactsSchema, } from '@postflop/schema';
import { deltaBucketOf, equityBucketOf, priceOf, sprBucketOf } from './digest';
/** Equity delta in percentage points between two streets (curr − prev), or undefined if no prev. */
function equityDeltaPoints(currEquity, prev) {
    if (!prev)
        return undefined;
    return currEquity * 100 - prev.hero.equity.equity * 100;
}
/**
 * Compute the cross-street `whatChanged` block (killer feature 1; ARCHITECTURE.md §9). The equity
 * delta and its bucket are computed from the two streets' hero-vs-range equities; the texture event is
 * this street's board event; range-advantage shift is read off the equity swing (hero.equity is
 * measured vs the villain range, so a swing IS a range-advantage move for hero's hand). `lines` are
 * factual summaries for the template layer (EngineFacts is the full record - the coach never sees
 * whatChanged; only its distilled `hero.equityDeltaBucket` reaches the digest).
 */
export function computeWhatChanged(prev, curr) {
    const deltaPts = curr.equity.equity * 100 - prev.hero.equity.equity * 100;
    const equityDeltaBucket = deltaBucketOf(deltaPts);
    const textureEvents = curr.streetEvent ? [curr.streetEvent] : [];
    const rangeAdvantageShift = deltaPts > 5 ? 'toHero' : deltaPts < -5 ? 'toVillain' : 'unchanged';
    const lines = [];
    const rounded = Math.round(deltaPts);
    if (equityDeltaBucket === 'held') {
        lines.push(`Equity held on the ${curr.street} (${rounded >= 0 ? '+' : ''}${rounded} pts).`);
    }
    else {
        lines.push(`Equity ${equityDeltaBucket} on the ${curr.street} (${rounded >= 0 ? '+' : ''}${rounded} pts).`);
    }
    if (curr.streetEvent) {
        lines.push(`Board event: ${curr.streetEvent.kind}${curr.streetEvent.completesFlush ? ', completes a flush' : ''}${curr.streetEvent.pairsBoard ? ', pairs the board' : ''}.`);
    }
    return { equityDeltaBucket, textureEvents, rangeAdvantageShift, lines };
}
/**
 * Assemble one decision's full EngineFacts from pre-computed engine outputs. Derives sprBucket,
 * equityBucket, the equity delta + bucket, and the price statement; carries the graded userAction when
 * present; computes whatChanged from `prev` when not supplied. Validates against EngineFactsSchema at
 * the boundary (repo posture: zod at every serialization boundary) and returns the parsed record.
 */
export function assembleFacts(input) {
    const price = priceOf(input.math.pot, input.math.toCall);
    const deltaPts = input.hero.equityDeltaPts ?? equityDeltaPoints(input.hero.equity.equity, input.prev);
    const equityDeltaBucket = deltaPts === undefined ? undefined : deltaBucketOf(deltaPts);
    const whatChanged = input.whatChanged ??
        (input.prev
            ? computeWhatChanged(input.prev, {
                equity: input.hero.equity,
                streetEvent: input.board.streetEvent,
                street: input.street,
            })
            : undefined);
    const facts = {
        v: 1,
        handId: input.handId,
        street: input.street,
        decisionIndex: input.decisionIndex,
        context: {
            gameType: input.context.gameType,
            mode: input.context.mode,
            position: input.context.position,
            positionClass: input.context.positionClass,
            playersInHand: input.context.playersInHand,
            icmPressure: input.context.icmPressure,
        },
        math: {
            pot: input.math.pot,
            toCall: input.math.toCall,
            effectiveStack: input.math.effectiveStack,
            spr: input.math.spr,
            sprBucket: sprBucketOf(input.math.spr),
            potOdds: price?.potOdds,
            requiredEquityPct: price?.requiredEquityPct,
        },
        hero: {
            holeCards: input.hero.holeCards,
            handClass: input.hero.handClass,
            drawFlags: input.hero.drawFlags,
            equity: input.hero.equity,
            equityBucket: equityBucketOf(input.hero.equity.equity),
            equityDelta: deltaPts,
            equityDeltaBucket,
        },
        board: {
            cards: input.board.cards,
            textureClass: input.board.textureClass,
            streetEvent: input.board.streetEvent,
        },
        villain: {
            seat: input.villain.seat,
            archetypeId: input.villain.archetype.id,
            imbalances: input.villain.archetype.imbalances,
            rangeShape: input.villain.rangeShape,
            bucketed: input.villain.bucketed,
        },
        heroPerceived: input.heroPerceived,
        recommendation: input.recommendation,
        whatChanged,
        icm: input.icm,
        userAction: input.grade?.userAction,
    };
    return EngineFactsSchema.parse(facts);
}
//# sourceMappingURL=assemble.js.map